import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Admin, Public } from "@/lib/api";
import { AdminLayout } from "@/pages/AdminPages";
import { Pagination } from "@/components/Pagination";
import { confirmDialog, customDialog, toast } from "@/components/ui/dialog";
import { JsonEditor } from "@/components/JsonEditor";
import { navigate } from "@/lib/router";
import { Mail } from "lucide-react";

// ---------------- 通用：JSON 编辑器（带高亮 / 错误标记 / 模板重置） ----------------
const JsonField = JsonEditor;

// ============================================================
// 栏目管理
// ============================================================
export function AdminCategoriesPage() {
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => Admin.categories().then(setList).catch((e) => setError(e.message));
  useEffect(() => { reload(); }, []);

  function blank() {
    setEditing({
      id: null, parent_id: null, slug: "", name: "", custom_id: "",
      sort: 0, is_active: true, filter_schema: [], list_columns: [], seo: {},
    });
  }

  async function save() {
    if (!editing) return;
    try {
      await Admin.saveCategory(editing.id, editing);
      setEditing(null); reload();
    } catch (e: any) { setError(e.message); }
  }
  async function del(id: string) {
    if (!(await confirmDialog({ title: "删除栏目？", description: "同时会影响其下项目的展示，请谨慎。", destructive: true, confirmText: "删除" }))) return;
    await Admin.delCategory(id); reload();
  }

  return (
    <AdminLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>栏目管理</CardTitle>
          <Button onClick={blank}>新增栏目</Button>
        </CardHeader>
        <CardContent>
          {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">名称</th><th>slug</th><th>custom_id</th><th>排序</th><th>启用</th><th></th>
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-b">
                  <td className="py-2">{c.name}</td>
                  <td className="font-mono text-xs">{c.slug}</td>
                  <td className="font-mono text-xs">{c.custom_id}</td>
                  <td>{c.sort}</td>
                  <td>{c.is_active ? "✓" : "—"}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })}>编辑</Button>
                    <Button size="sm" variant="destructive" onClick={() => del(c.id)}>删除</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editing && (
        <Card className="mt-4">
          <CardHeader><CardTitle>{editing.id ? "编辑" : "新增"}栏目</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Field label="名称"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="slug (URL 标识)"><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></Field>
            <Field label="custom_id (爬虫使用)"><Input value={editing.custom_id} onChange={(e) => setEditing({ ...editing, custom_id: e.target.value })} /></Field>
            <Field label="排序"><Input type="number" value={editing.sort ?? 0} onChange={(e) => setEditing({ ...editing, sort: +e.target.value })} /></Field>
            <Field label="父栏目 id (UUID, 可空)"><Input value={editing.parent_id ?? ""} onChange={(e) => setEditing({ ...editing, parent_id: e.target.value || null })} /></Field>
            <Field label="启用">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.is_active ? "1" : "0"}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.value === "1" })}>
                <option value="1">启用</option><option value="0">停用</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <Label>动态筛选字段 filter_schema (JSON Array)</Label>
              <p className="text-xs text-muted-foreground mb-1">
                例如 <code>[{`{"key":"year","label":"年份","type":"select","options":["2024","2025","2026"]}`}]</code>
              </p>
              <JsonField value={editing.filter_schema} onChange={(v) => setEditing({ ...editing, filter_schema: v })} />
            </div>
            <div className="md:col-span-2">
              <Label>列表展示列 list_columns (JSON)</Label>
              <JsonField value={editing.list_columns} onChange={(v) => setEditing({ ...editing, list_columns: v })} />
            </div>
            <div className="md:col-span-2">
              <Label>SEO (title/description/keywords)</Label>
              <JsonField value={editing.seo} onChange={(v) => setEditing({ ...editing, seo: v })} />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={save}>保存</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>取消</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><Label>{label}</Label>{children}</div>;
}

// ---------------- 角色多选弹窗 ----------------
const ROLE_OPTIONS: { key: string; name: string; desc: string }[] = [
  { key: "admin",  name: "超级管理员", desc: "拥有所有后台权限" },
  { key: "editor", name: "内容编辑",   desc: "可管理内容与栏目" },
  { key: "vip",    name: "VIP 会员",   desc: "可访问付费内容" },
  { key: "member", name: "注册会员",   desc: "默认角色，访问免费内容" },
];

function openRolesDialog(email: string, current: string[]): Promise<string[] | null> {
  return customDialog<string[]>((close) => (
    <RolesPicker email={email} current={current} onConfirm={(v) => close(v)} onCancel={() => close(null)} />
  ));
}

function RolesPicker({ email, current, onConfirm, onCancel }: { email: string; current: string[]; onConfirm: (v: string[]) => void; onCancel: () => void }) {
  const [picked, setPicked] = useState<Set<string>>(new Set(current));
  const toggle = (k: string) => {
    const next = new Set(picked);
    if (next.has(k)) next.delete(k); else next.add(k);
    setPicked(next);
  };
  return (
    <div className="rounded-xl bg-card shadow-2xl border w-full max-w-md overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b">
        <div className="font-semibold">编辑用户角色</div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate">{email}</div>
      </div>
      <div className="p-3 space-y-1.5 max-h-[60vh] overflow-y-auto">
        {ROLE_OPTIONS.map((r) => {
          const on = picked.has(r.key);
          return (
            <label key={r.key}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${on ? "border-primary bg-primary/5 ring-1 ring-primary/40" : "hover:bg-muted/50"}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(r.key)} className="mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium">{r.name} <code className="ml-1 text-[10px] text-muted-foreground">{r.key}</code></div>
                <div className="text-xs text-muted-foreground">{r.desc}</div>
              </div>
            </label>
          );
        })}
      </div>
      <div className="px-5 py-3 bg-muted/30 border-t flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>取消</Button>
        <Button size="sm" onClick={() => onConfirm(Array.from(picked))}>保存</Button>
      </div>
    </div>
  );
}

// ============================================================
// 项目管理
// ============================================================
export function AdminProjectsPage() {
  const [data, setData] = useState<{ items: any[]; page: number; total: number }>({ items: [], page: 1, total: 0 });
  const [q, setQ] = useState(""); const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<any | null>(null);
  const reload = () => Admin.projects({ q, status, page, page_size: 20 }).then(setData);
  useEffect(() => { reload(); }, [page]);

  return (
    <AdminLayout>
      <Card>
        <CardHeader><CardTitle>内容管理</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3">
            <Input placeholder="搜索标题" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
            <select className="h-9 rounded-md border bg-background px-3" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">全部状态</option><option value="published">已发布</option>
              <option value="draft">草稿</option><option value="archived">已归档</option>
            </select>
            <Button onClick={() => { setPage(1); reload(); }}>查询</Button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">标题</th><th>分类</th><th>状态</th><th>付费</th><th>发布日期</th><th></th>
            </tr></thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="py-2 max-w-md truncate">{p.title}</td>
                  <td>{p.category}</td>
                  <td>{p.status}</td>
                  <td>{p.paywall_override || "—"}</td>
                  <td className="text-xs">{p.published_at?.slice(0, 10) ?? "-"}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })}>编辑</Button>
                    <Button size="sm" variant="destructive" onClick={async () => {
                      if (await confirmDialog({ title: "删除该项目？", description: p.title, destructive: true, confirmText: "删除" })) { await Admin.delProject(p.id); reload(); }
                    }}>删除</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pt-3">
            <Pagination page={page} total={data.total} pageSize={20} onChange={setPage} />
          </div>
        </CardContent>
      </Card>

      {editing && (
        <Card className="mt-4">
          <CardHeader><CardTitle>编辑：{editing.title}</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Field label="标题"><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="状态">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.status}
                onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="published">published</option><option value="draft">draft</option><option value="archived">archived</option>
              </select>
            </Field>
            <Field label="付费覆写 paywall_override (inherit|free|guest|login|paid)">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.paywall_override ?? "inherit"}
                onChange={(e) => setEditing({ ...editing, paywall_override: e.target.value })}>
                <option value="inherit">inherit (跟随规则)</option>
                <option value="free">free 完全公开</option>
                <option value="guest">guest 游客可见</option>
                <option value="login">login 仅需登录</option>
                <option value="paid">paid 强制付费</option>
              </select>
            </Field>
            <div className="md:col-span-2"><Field label="摘要">
              <Textarea rows={3} value={editing.summary ?? ""} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
            </Field></div>
            <div className="md:col-span-2"><Field label="正文 HTML">
              <Textarea rows={10} value={editing.content_html ?? ""} onChange={(e) => setEditing({ ...editing, content_html: e.target.value })} className="font-mono text-xs" />
            </Field></div>
            <div className="md:col-span-2">
              <Label>动态字段 dynamic_fields</Label>
              <JsonField value={editing.dynamic_fields ?? {}} onChange={(v) => setEditing({ ...editing, dynamic_fields: v })} />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={async () => {
                await Admin.saveProject(editing.id, {
                  title: editing.title, summary: editing.summary, content_html: editing.content_html,
                  status: editing.status, paywall_override: editing.paywall_override,
                  dynamic_fields: editing.dynamic_fields,
                });
                setEditing(null); reload();
              }}>保存</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>取消</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}

// ============================================================
// 付费规则
// ============================================================
export function AdminPaywallPage() {
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const reload = () => Admin.rules().then(setList);
  useEffect(() => { reload(); }, []);

  function blank() {
    setEditing({
      id: null, name: "", scope: "global",
      match: {}, require: { plan_codes: ["pro"] },
      priority: 100, enabled: true, note: "",
    });
  }
  return (
    <AdminLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>付费规则</CardTitle><Button onClick={blank}>新增规则</Button>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">
            规则按优先级降序匹配；scope 可选：<code>global</code> / <code>category</code> / <code>field</code> / <code>year_range</code> / <code>project</code>。
          </p>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">名称</th><th>scope</th><th>优先级</th><th>启用</th><th></th>
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="py-2">{r.name}</td><td>{r.scope}</td>
                  <td>{r.priority}</td><td>{r.enabled ? "✓" : "—"}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...r })}>编辑</Button>
                    <Button size="sm" variant="destructive" onClick={async () => {
                      if (await confirmDialog({ title: "删除付费规则？", description: r.name, destructive: true, confirmText: "删除" })) { await Admin.delRule(r.id); reload(); }
                    }}>删除</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editing && (
        <Card className="mt-4">
          <CardHeader><CardTitle>{editing.id ? "编辑" : "新增"}规则</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Field label="名称"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="scope">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.scope}
                onChange={(e) => setEditing({ ...editing, scope: e.target.value })}>
                <option value="global">global</option>
                <option value="category">category</option>
                <option value="field">field</option>
                <option value="year_range">year_range</option>
                <option value="project">project</option>
              </select>
            </Field>
            <Field label="优先级"><Input type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: +e.target.value })} /></Field>
            <Field label="启用">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.enabled ? "1" : "0"}
                onChange={(e) => setEditing({ ...editing, enabled: e.target.value === "1" })}>
                <option value="1">启用</option><option value="0">停用</option>
              </select>
            </Field>
            <div className="md:col-span-2">
              <Label>匹配条件 match (JSON)</Label>
              <p className="text-xs text-muted-foreground mb-1">
                例：<code>{`{"category_custom_ids":["zhongbiao"]}`}</code>、
                <code>{`{"field":"year","gte":2024}`}</code>
              </p>
              <JsonField value={editing.match} onChange={(v) => setEditing({ ...editing, match: v })} />
            </div>
            <div className="md:col-span-2">
              <Label>授权要求 require (JSON)</Label>
              <p className="text-xs text-muted-foreground mb-1">
                例：<code>{`{"plan_codes":["pro","ultimate"]}`}</code>
              </p>
              <JsonField value={editing.require} onChange={(v) => setEditing({ ...editing, require: v })} />
            </div>
            <Field label="备注"><Input value={editing.note ?? ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} /></Field>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={async () => {
                await Admin.saveRule(editing.id, editing); setEditing(null); reload();
              }}>保存</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>取消</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}

// ============================================================
// 套餐
// ============================================================
export function AdminPlansPage() {
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const reload = () => Admin.plans().then(setList);
  useEffect(() => { reload(); }, []);

  function blank() { setEditing({ id: null, code: "", name: "", price_cents: 0, currency: "CNY", period: "year", benefits: { items: [] } }); }

  return (
    <AdminLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>会员套餐</CardTitle><Button onClick={blank}>新增套餐</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">code</th><th>名称</th><th>价格</th><th>周期</th><th></th>
            </tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="py-2 font-mono text-xs">{p.code}</td><td>{p.name}</td>
                  <td>{(p.price_cents / 100).toFixed(2)} {p.currency}</td><td>{p.period}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })}>编辑</Button>
                    <Button size="sm" variant="destructive" onClick={async () => {
                      if (await confirmDialog({ title: "下架该会员套餐？", description: p.name, destructive: true, confirmText: "下架" })) { await Admin.delPlan(p.id); reload(); }
                    }}>下架</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editing && (
        <Card className="mt-4">
          <CardHeader><CardTitle>{editing.id ? "编辑" : "新增"}套餐</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Field label="code"><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></Field>
            <Field label="名称"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="价格 (分)"><Input type="number" value={editing.price_cents} onChange={(e) => setEditing({ ...editing, price_cents: +e.target.value })} /></Field>
            <Field label="货币"><Input value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} /></Field>
            <Field label="周期">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.period}
                onChange={(e) => setEditing({ ...editing, period: e.target.value })}>
                <option value="month">month</option><option value="quarter">quarter</option>
                <option value="year">year</option><option value="lifetime">lifetime</option>
              </select>
            </Field>
            <div className="md:col-span-2"><Field label="描述">
              <Textarea rows={2} value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </Field></div>
            <div className="md:col-span-2">
              <Label>权益 benefits (JSON)</Label>
              <JsonField value={editing.benefits} onChange={(v) => setEditing({ ...editing, benefits: v })} />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={async () => { await Admin.savePlan(editing.id, editing); setEditing(null); reload(); }}>保存</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>取消</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}

// ============================================================
// 积分套餐
// ============================================================
export function AdminPointPackagesPage() {
  const [list, setList] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const reload = () => Admin.pointPackages().then(setList);
  useEffect(() => { reload(); }, []);

  function blank() {
    setEditing({ id: null, code: "", name: "", description: "", points: 100, bonus: 0, price_cents: 1000, currency: "CNY", is_active: true, sort: 0 });
  }

  return (
    <AdminLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>积分套餐</CardTitle><Button onClick={blank}>新增套餐</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">code</th><th>名称</th><th>积分</th><th>赠送</th><th>价格</th><th>状态</th><th></th>
            </tr></thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="py-2 font-mono text-xs">{p.code}</td>
                  <td>{p.name}</td>
                  <td>{p.points}</td>
                  <td>{p.bonus || 0}</td>
                  <td>{(p.price_cents / 100).toFixed(2)} {p.currency}</td>
                  <td>{p.is_active ? "启用" : "停用"}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })}>编辑</Button>
                    <Button size="sm" variant="destructive" onClick={async () => {
                      if (await confirmDialog({ title: "下架该积分套餐？", description: p.name, destructive: true, confirmText: "下架" })) { await Admin.delPointPackage(p.id); reload(); }
                    }}>下架</Button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">暂无套餐</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editing && (
        <Card className="mt-4">
          <CardHeader><CardTitle>{editing.id ? "编辑" : "新增"}积分套餐</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <Field label="code"><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></Field>
            <Field label="名称"><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="积分数"><Input type="number" value={editing.points} onChange={(e) => setEditing({ ...editing, points: +e.target.value })} /></Field>
            <Field label="赠送积分"><Input type="number" value={editing.bonus ?? 0} onChange={(e) => setEditing({ ...editing, bonus: +e.target.value })} /></Field>
            <Field label="价格 (分)"><Input type="number" value={editing.price_cents} onChange={(e) => setEditing({ ...editing, price_cents: +e.target.value })} /></Field>
            <Field label="货币"><Input value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} /></Field>
            <Field label="排序"><Input type="number" value={editing.sort ?? 0} onChange={(e) => setEditing({ ...editing, sort: +e.target.value })} /></Field>
            <Field label="状态">
              <select className="h-9 rounded-md border bg-background px-3" value={editing.is_active ? "1" : "0"}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.value === "1" })}>
                <option value="1">启用</option><option value="0">停用</option>
              </select>
            </Field>
            <div className="md:col-span-2"><Field label="描述">
              <Textarea rows={2} value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </Field></div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={async () => { await Admin.savePointPackage(editing.id, editing); setEditing(null); reload(); }}>保存</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>取消</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}

// ============================================================
// API Keys
// ============================================================
export function AdminApiKeysPage() {
  const [list, setList] = useState<any[]>([]);
  const [cats, setCats] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [created, setCreated] = useState<string | null>(null);
  const reload = () => Admin.apiKeys().then(setList);
  useEffect(() => {
    reload();
    Public.categories().then(setCats);
  }, []);

  // 真实栏目 + 虚拟栏目（中标快讯专用：候选人 / 中标公告）
  const VIRTUAL = [
    { custom_id: "WINNER_CANDIDATE", name: "中标候选人公示（虚拟）" },
    { custom_id: "WINNER_BID", name: "中标公告 / 结果（虚拟）" },
  ];
  const all = [...cats.filter((c: any) => c.custom_id), ...VIRTUAL];

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };
  const allChecked = all.length > 0 && all.every((c) => picked.has(c.custom_id));
  const toggleAll = () => {
    if (allChecked) setPicked(new Set());
    else setPicked(new Set(all.map((c) => c.custom_id)));
  };

  return (
    <AdminLayout>
      <Card>
        <CardHeader><CardTitle>API 密钥（爬虫上传）</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-[1fr_auto] mb-4 items-start">
            <Input placeholder="名称" value={name} onChange={(e) => setName(e.target.value)} />
            <Button onClick={async () => {
              if (!name.trim()) { toast.warning("请填写名称"); return; }
              try {
                const r = await Admin.createApiKey({ name, allowed_categories: Array.from(picked) });
                setCreated(r.key); setName(""); setPicked(new Set()); reload();
              } catch (e: any) { toast.error(e.message); }
            }}>签发密钥</Button>
          </div>

          <div className="border rounded-md p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">允许上传的栏目</Label>
              <button type="button" onClick={toggleAll} className="text-xs text-primary hover:underline">
                {allChecked ? "全不选" : "全选"}
              </button>
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 max-h-72 overflow-y-auto">
              {all.map((c) => (
                <label key={c.custom_id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-2 py-1 hover:bg-muted/50">
                  <input type="checkbox" checked={picked.has(c.custom_id)} onChange={() => toggle(c.custom_id)} />
                  <span className="truncate">{c.name}</span>
                  <code className="text-[10px] text-muted-foreground ml-auto">{c.custom_id}</code>
                </label>
              ))}
              {all.length === 0 && <span className="text-sm text-muted-foreground">暂无栏目，请先创建</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              留空表示允许上传到所有栏目。虚拟栏目用于在 ingest 时把数据写入 <code>dynamic_fields.type</code> 为对应值。
            </p>
          </div>

          {created && (
            <div className="rounded-md border border-yellow-500 bg-yellow-50 p-3 text-sm mb-4">
              <p className="font-medium text-yellow-900">请立即复制密钥（仅显示一次）：</p>
              <code className="block break-all mt-1 font-mono">{created}</code>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => setCreated(null)}>已保存</Button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">名称</th><th>前缀</th><th>限速</th><th>启用</th><th>最后使用</th><th></th>
            </tr></thead>
            <tbody>
              {list.map((k) => (
                <tr key={k.id} className="border-b">
                  <td className="py-2">{k.name}</td><td className="font-mono text-xs">{k.prefix}…</td>
                  <td>{k.rate_limit_per_min}/min</td>
                  <td>
                    {k.is_active
                      ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">● 启用</span>
                      : <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">○ 已吊销</span>}
                  </td>
                  <td className="text-xs">{k.last_used_at?.slice(0, 19) ?? "-"}</td>
                  <td className="text-right">
                    {k.is_active ? (
                      <Button size="sm" variant="destructive" onClick={async () => {
                        if (await confirmDialog({ title: "吊销该密钥？", description: `吊销后该密钥将立即失效，所属爬虫无法上传数据。\n${k.name}`, destructive: true, confirmText: "吊销" })) {
                          try { await Admin.delApiKey(k.id); toast.success("密钥已吊销"); reload(); }
                          catch (e: any) { toast.error(e?.message || "吊销失败"); }
                        }
                      }}>吊销</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (await confirmDialog({ title: "重新颁发密钥？", description: `将生成新的密钥替换旧值，其它配置（栏目权限、限速等）保持不变。\n${k.name}`, confirmText: "重新颁发" })) {
                          try {
                            const r = await Admin.reissueApiKey(k.id);
                            setCreated(r.key);
                            toast.success("已重新颁发");
                            reload();
                          } catch (e: any) { toast.error(e?.message || "颁发失败"); }
                        }
                      }}>重新颁发</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

// ============================================================
// 用户
// ============================================================
export function AdminUsersPage() {
  const [data, setData] = useState<{ items: any[]; total: number } | null>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const reload = () => Admin.users({ q, page, page_size: PAGE_SIZE }).then(setData);
  useEffect(() => { reload(); }, [page]);

  return (
    <AdminLayout>
      <Card>
        <CardHeader><CardTitle>用户管理</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3">
            <Input placeholder="按邮箱/手机搜索" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
            <Button onClick={() => { setPage(1); reload(); }}>查询</Button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left">
              <th className="py-2">邮箱</th><th>角色</th><th>状态</th><th>注册时间</th><th>最后登录</th><th></th>
            </tr></thead>
            <tbody>
              {(data?.items || []).map((u) => (
                <tr key={u.id} className="border-b">
                  <td className="py-2">{u.email}</td>
                  <td className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).length === 0 && <span className="text-muted-foreground">—</span>}
                      {(u.roles || []).map((r: string) => (
                        <span key={r} className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] bg-muted">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td>{u.status}</td>
                  <td className="text-xs">{u.created_at?.slice(0, 10)}</td>
                  <td className="text-xs">{u.last_login_at?.slice(0, 19) ?? "-"}</td>
                  <td className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => navigate("/admin/inbox-send", { email: u.email })}
                            title="发送站内信">
                      <Mail className="h-3.5 w-3.5 mr-1" /> 发站内信
                    </Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      const next = await openRolesDialog(u.email, u.roles || []);
                      if (!next) return;
                      const cur = new Set<string>(u.roles || []);
                      const wanted = new Set<string>(next);
                      const toAdd = [...wanted].filter((r) => !cur.has(r));
                      const toRemove = [...cur].filter((r) => !wanted.has(r));
                      try {
                        for (const r of toAdd) await Admin.setUserRole(u.id, r, "add");
                        for (const r of toRemove) await Admin.setUserRole(u.id, r, "remove");
                        toast.success("角色已更新");
                        reload();
                      } catch (e: any) {
                        toast.error(e?.message || "更新失败");
                      }
                    }}>编辑角色</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && (
            <div className="pt-3">
              <Pagination page={page} total={data.total} pageSize={PAGE_SIZE} onChange={setPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

// ============================================================
// 站点设置
// ============================================================
const SETTING_TEMPLATES: Record<string, string> = {
  "site.basic": `{
  // 站点名称（页头/页脚显示）
  "name": "招投通",
  // 站点 Logo URL（留空则用文字 Logo）
  "logo": "",
  // ICP 备案号
  "icp": "",
  // 页脚版权信息
  "copyright": ""
}`,
  "seo.default": `{
  // 默认页面标题
  "title": "招投通 - 招投标信息汇总",
  // SEO 关键词，逗号分隔
  "keywords": "招标,中标,采购",
  // SEO 描述
  "description": "全网招投中标信息聚合"
}`,
  "register": `{
  // 是否开放注册（false 时所有注册接口直接拒绝）
  "enabled": true,
  // 注册防机器人验证码：none | image | turnstile | geetest
  // 选择 turnstile / geetest 时，需在「captcha.turnstile」/「captcha.geetest」中配置服务商参数
  "captcha": "image",
  // 是否启用邮箱验证码（需先在 smtp 中配置邮件服务，否则运行期自动关闭）
  "verify_email": false,
  // 是否启用短信验证码（需先在 sms.aliyun 中配置短信服务，否则运行期自动关闭）
  "verify_sms": false,
  // 是否仅邀请制（暂未启用）
  "invite_only": false,
  // 是否允许邮箱注册
  "allow_email": true,
  // 是否允许手机号注册
  "allow_phone": true
}`,
  "login": `{
  // 登录验证码：none | image | turnstile | geetest
  // 选择 turnstile / geetest 时，需在「captcha.turnstile」/「captcha.geetest」中配置服务商参数
  "captcha": "none"
}`,
  "maintenance": `{
  // 是否进入维护模式（除管理员外所有 API 返回 503）
  "enabled": false,
  // 维护模式下展示给用户的文案
  "message": "系统维护中，预计 1 小时后恢复"
}`,
  "payment.providers": `{
  // Stripe（国际信用卡）
  "stripe": {
    "enabled": false,
    "publishable_key": "",      // pk_xxx，前端可见
    "secret_key": "",            // sk_xxx，仅后端使用
    "webhook_secret": ""         // whsec_xxx，验签 webhook
  },
  // 支付宝（PC 网站支付）
  "alipay": {
    "enabled": false,
    "app_id": "",                // 应用 APPID
    "private_key": "",           // 应用私钥（PEM 一行）
    "public_key": ""             // 支付宝公钥（PEM 一行）
  },
  // 微信支付 V3
  "wechat": {
    "enabled": false,
    "app_id": "",
    "mch_id": "",
    "api_key": ""
  }
}`,
  "smtp": `{
  // 是否启用 SMTP 发送（false 时邮件验证码相关功能不可用）
  "enabled": true,
  // SMTP 服务器地址，例如 smtp.qq.com / smtp.exmail.qq.com / smtp.sendgrid.net
  "host": "",
  // 端口：465 走 SMTPS（implicit TLS），587 走 STARTTLS
  "port": 465,
  // 登录用户名（一般等于发件邮箱）
  "username": "",
  // 登录密码或授权码（不是邮箱登录密码！需到邮箱后台开启 SMTP 后获取授权码）
  "password": "",
  // 发件人，格式 "显示名 <addr@example.com>"，如 "招投通 <noreply@example.com>"
  "from": "",
  // 是否使用 STARTTLS（端口 587 时设为 true，端口 465 时保持 false）
  "starttls": false
}`,
  "sms.aliyun": `{
  // 是否启用阿里云短信
  "enabled": true,
  // 阿里云访问凭据
  "access_key_id": "",
  "access_key_secret": "",
  // 已审核通过的签名（如「招投通」）
  "sign_name": "",
  // 已审核通过的模板 Code（如 SMS_xxx）
  "template_code": "",
  // API 端点，一般无需修改
  "endpoint": "https://dysmsapi.aliyuncs.com"
}`,
  "captcha.turnstile": `{
  // 是否启用 Cloudflare Turnstile 服务（仅做校验开关；最终是否启用由 login/register 的 captcha 字段决定）
  "enabled": true,
  // 控制台获取的站点密钥（前端可见）
  "site_key": "",
  // 控制台获取的服务端密钥（仅后端使用）
  "secret": "",
  // 验签 API；自托管 / 海外节点等情况下可改写
  "verify_url": "https://challenges.cloudflare.com/turnstile/v0/siteverify"
}`,
  "captcha.geetest": `{
  // 是否启用极验 V4（仅做校验开关；最终是否启用由 login/register 的 captcha 字段决定）
  "enabled": true,
  // 极验后台「验证 ID」
  "captcha_id": "",
  // 极验后台「验证 Key」
  "captcha_key": "",
  // API 端点，一般无需修改
  "api_url": "https://gcaptcha4.geetest.com"
}`,
  "s3": `{
  // 自定义 endpoint（MinIO / R2 / 阿里云 OSS / 腾讯 COS 等）。AWS S3 留空使用区域默认值。
  "endpoint": "",
  // 区域，例如 us-east-1 / ap-east-1 / auto（R2）
  "region": "",
  // Bucket 名称
  "bucket": "",
  // 访问密钥
  "access_key": "",
  "secret_key": "",
  // 公网访问基地址；启用 CDN/自定义域名时填，否则留空使用默认对象 URL
  "public_base": "",
  // 是否使用 path-style URL（MinIO / R2 必须 true；S3 可设 false）
  "force_path_style": true
}`,
  subscription: `{
  // 总开关；关闭后不会调度任何推送
  "enabled": true,
  // 每次成功推送扣除用户多少积分
  "points_per_push": 5,
  // 单个用户最多多少条订阅规则
  "max_rules_per_user": 50,
  // 是否要求创建订阅规则的用户拥有有效 VIP
  "require_active_vip": true
}`,
  welcome_gift: `{
  // 开关；开启后未登录访客会看到赠礼弹窗，新注册用户也会自动领取一次
  "enabled": false,
  // VIP 方案代码（membership_plans.code），留空则不赠送 VIP
  "vip_plan_code": "",
  // VIP 赠送天数（0 表示不限时，但需要 vip_plan_code 存在）
  "vip_days": 7,
  // 赠送积分数量（0 表示不赠积分）
  "points": 100,
  // 弹窗标题
  "hero_title": "新用户专享好礼",
  // 弹窗文案（支持 \\n 换行）
  "hero_message": "注册即领 VIP 体验 + 体验积分，解锁全量招标信息。",
  // 主按钮文案
  "cta_label": "立即领取"
}`,
  inbox_rules: `{
  // VIP 到期提醒提前多少天发送（可多个）
  "vip_expiring_days": [7, 3, 1],
  // VIP 到期提醒是否同步发邮件
  "vip_expiring_also_email": true,
  // 积分低于多少时提醒（0 为关闭）
  "low_points_threshold": 10,
  // 积分不足提醒是否同步发邮件
  "low_points_also_email": false,
  // 订阅推送命中后是否同时写一条站内信
  "subscription_to_inbox": true,
  // VIP 过期后还可推送多少次（宽限期）
  "grace_pushes_after_expiry": 3,
  // 积分不足后还可推送多少次（宽限期）
  "grace_pushes_low_points": 3,
  // 模板（可用占位符 {days}/{points}/{remaining}）
  "vip_expiring_template": "您的 VIP 将于 {days} 天后到期，请及时续费。",
  "low_points_template": "您的积分仅剩 {points}，为避免推送中断，请及时充值。",
  "vip_expired_grace_template": "您的 VIP 已过期。本次推送仍按宽限策略发送，您还可收到 {remaining} 次。",
  "low_points_grace_template": "您的积分不足。本次按宽限策略发送，您还可收到 {remaining} 次。"
}`,
};
const SETTING_KEYS: [string, string][] = [
  ["site.basic", "站点基本信息（名称/Logo/备案号）"],
  ["seo.default", "SEO 默认（title/description/keywords）"],
  ["register", "注册开关（验证码 + 邮箱/短信验证码）"],
  ["login", "登录验证码（captcha = none/image/turnstile/geetest）"],
  ["maintenance", "维护模式（enabled / message）"],
  ["payment.providers", "支付配置（stripe/alipay/wechat 密钥）"],
  ["smtp", "SMTP 邮件（host/port/username/password/from）"],
  ["sms.aliyun", "阿里云短信（access_key_id/secret/sign_name/template_code）"],
  ["captcha.turnstile", "Cloudflare Turnstile（site_key/secret）"],
  ["captcha.geetest", "极验 V4（captcha_id/captcha_key）"],
  ["s3", "对象存储（endpoint/region/bucket/access/secret）"],
  ["subscription", "订阅推送（启用 / 每次推送扣费 / 仅 VIP）"],
  ["inbox_rules", "站内信规则（VIP/积分提醒 + 宽限期 + 模板）"],
  ["welcome_gift", "新用户赠礼（赠送 VIP 天数 / 积分 / 弹窗文案）"],
];
export function AdminSettingsPage() {
  const [key, setKey] = useState("site.basic");
  const [value, setValue] = useState<any>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSaved(false);
    // 后端值为空时给 JsonEditor 传入 {}，让其首次显示 JSONC 模板（带注释说明）
    Admin.getSetting(key).then((r) => setValue(r.value ?? {})).catch(() => setValue({}));
  }, [key]);

  return (
    <AdminLayout>
      <Card>
        <CardHeader><CardTitle>站点设置</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-center">
            <Label>选择配置项</Label>
            <select className="h-9 rounded-md border bg-background px-3 min-w-[280px]" value={key}
              onChange={(e) => setKey(e.target.value)}>
              {SETTING_KEYS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">键：<code>{key}</code></p>
          <JsonEditor key={key} value={value} onChange={setValue} rows={16}
            template={SETTING_TEMPLATES[key]}
            onReset={() => toast.info("已重置为初始模板，记得点击保存")} />
          <div className="flex gap-2">
            <Button onClick={async () => {
              const r: any = await Admin.setSetting(key, value);
              setSaved(true);
              if (r?.warning) toast.warning(String(r.warning));
              setTimeout(() => setSaved(false), 2000);
            }}>保存</Button>
            {saved && <span className="text-sm text-green-600 self-center">已保存</span>}
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

// ============================================================
// 维护模式
// ============================================================
export function AdminMaintenancePage() {
  const [enabled, setEnabled] = useState(false); const [message, setMessage] = useState("");
  useEffect(() => {
    Admin.getSetting("maintenance").then((r) => {
      setEnabled(r.value?.enabled ?? false);
      setMessage(r.value?.message ?? "");
    }).catch(() => {});
  }, []);
  return (
    <AdminLayout>
      <Card>
        <CardHeader><CardTitle>维护模式</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            开启后，普通用户访问 SPA / SSR 页面会看到维护提示；管理员仍可访问 <code>/admin</code> 与 <code>/api/admin/*</code>。
          </p>
          <Field label="维护提示文案">
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <Button onClick={async () => { await Admin.setMaintenance(true, message); setEnabled(true); }} variant="destructive">
              开启维护模式
            </Button>
            <Button onClick={async () => { await Admin.setMaintenance(false, message); setEnabled(false); }} variant="outline">
              关闭维护模式
            </Button>
          </div>
          <div className="text-sm">当前状态：
            <span className={enabled ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
              {enabled ? "维护中" : "正常运行"}
            </span>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

// ============================================================
// 数据分析
// ============================================================
export function AdminAnalyticsPage() {
  const [summary, setSummary] = useState<any>({});
  const [yearly, setYearly] = useState<any[]>([]);
  const [provinces, setProvinces] = useState<any[]>([]);
  const [bidders, setBidders] = useState<any[]>([]);
  useEffect(() => {
    Admin.analyticsSummary().then(setSummary).catch(() => {});
    Admin.analyticsYearly().then(setYearly).catch(() => {});
    Admin.analyticsByProvince().then(setProvinces).catch(() => {});
    Admin.analyticsTopBidders().then(setBidders).catch(() => {});
  }, []);

  const max = (arr: any[], k: string) => Math.max(1, ...arr.map((r) => Number(r[k]) || 0));
  const sum = (arr: any[], k: string) => arr.reduce((a, r) => a + (Number(r[k]) || 0), 0);

  const PALETTE = [
    "from-indigo-500 to-indigo-400",
    "from-sky-500 to-sky-400",
    "from-emerald-500 to-emerald-400",
    "from-amber-500 to-amber-400",
    "from-rose-500 to-rose-400",
    "from-fuchsia-500 to-fuchsia-400",
    "from-cyan-500 to-cyan-400",
    "from-lime-500 to-lime-400",
  ];

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-5">
          {[
            ["项目总数", summary.total_projects, "from-indigo-500 to-indigo-400"],
            ["注册用户", summary.total_users, "from-sky-500 to-sky-400"],
            ["付费订单", summary.total_paid_orders, "from-emerald-500 to-emerald-400"],
            ["营收 (元)", ((summary.revenue_cents || 0) / 100).toFixed(2), "from-amber-500 to-amber-400"],
            ["有效会员", summary.active_members, "from-rose-500 to-rose-400"],
          ].map(([label, v, tone]) => (
            <Card key={label as string} className="relative overflow-hidden">
              <div className={`absolute inset-0 -z-10 opacity-10 bg-gradient-to-br ${tone}`} />
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-2xl font-semibold mt-1">{v ?? "-"}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>年度发布量趋势</CardTitle></CardHeader>
            <CardContent>
              <LineChart rows={yearly} labelKey="year" valueKey="count" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>省份分布 Top 10</CardTitle></CardHeader>
            <CardContent>
              <DonutChart rows={provinces.slice(0, 10)} labelKey="province" valueKey="count" total={sum(provinces, "count")} palette={PALETTE} />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle>省份分布 Top 50</CardTitle></CardHeader>
          <CardContent>
            <BarChart rows={provinces} labelKey="province" valueKey="count" max={max(provinces, "count")} palette={PALETTE} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>中标人 Top 50</CardTitle></CardHeader>
          <CardContent>
            <BarChart rows={bidders} labelKey="bidder" valueKey="count" max={max(bidders, "count")} palette={PALETTE} />
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function BarChart({ rows, labelKey, valueKey, max, palette }: { rows: any[]; labelKey: string; valueKey: string; max: number; palette: string[] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">暂无数据</p>;
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0;
        const pct = (v / max) * 100;
        const tone = palette[i % palette.length];
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-32 truncate text-muted-foreground">{r[labelKey] ?? "-"}</div>
            <div className="flex-1 bg-muted/60 rounded h-6 overflow-hidden">
              <div className={`h-6 rounded bg-gradient-to-r ${tone} text-white px-2 text-[11px] flex items-center font-medium shadow-sm transition-all`}
                style={{ width: `${pct}%`, minWidth: pct > 0 ? "32px" : "0" }}>{v}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ rows, labelKey, valueKey, total, palette }: { rows: any[]; labelKey: string; valueKey: string; total: number; palette: string[] }) {
  if (rows.length === 0 || total === 0) return <p className="text-sm text-muted-foreground">暂无数据</p>;
  let acc = 0;
  const stops = rows.map((r, i) => {
    const v = Number(r[valueKey]) || 0;
    const start = (acc / total) * 360;
    acc += v;
    const end = (acc / total) * 360;
    const colors = ["#6366f1","#0ea5e9","#10b981","#f59e0b","#f43f5e","#d946ef","#06b6d4","#84cc16","#a855f7","#14b8a6"];
    return { color: colors[i % colors.length], start, end, label: r[labelKey], v };
  });
  const gradient = stops.map(s => `${s.color} ${s.start}deg ${s.end}deg`).join(", ");
  const _ = palette;
  return (
    <div className="flex items-center gap-5">
      <div className="relative shrink-0" style={{ width: 160, height: 160 }}>
        <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(${gradient})` }} />
        <div className="absolute inset-6 rounded-full bg-card flex flex-col items-center justify-center">
          <div className="text-xs text-muted-foreground">合计</div>
          <div className="text-lg font-bold">{total}</div>
        </div>
      </div>
      <ul className="flex-1 space-y-1 text-xs">
        {stops.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
            <span className="flex-1 truncate">{s.label}</span>
            <span className="text-muted-foreground tabular-nums">{s.v} · {((s.v / total) * 100).toFixed(1)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LineChart({ rows, labelKey, valueKey }: { rows: any[]; labelKey: string; valueKey: string }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">暂无数据</p>;
  const W = 600, H = 200, P = 28;
  const data = rows.map((r) => ({ label: String(r[labelKey] ?? ""), v: Number(r[valueKey]) || 0 }));
  const maxV = Math.max(1, ...data.map((d) => d.v));
  const stepX = data.length > 1 ? (W - P * 2) / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = P + i * stepX;
    const y = H - P - (d.v / maxV) * (H - P * 2);
    return { x, y, ...d };
  });
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const last = points[points.length - 1]!;
  const first = points[0]!;
  const areaD = `${pathD} L ${last.x.toFixed(1)} ${H - P} L ${first.x.toFixed(1)} ${H - P} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <defs>
        <linearGradient id="lc-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = H - P - t * (H - P * 2);
        return <line key={t} x1={P} x2={W - P} y1={y} y2={y} stroke="currentColor" strokeOpacity="0.08" />;
      })}
      <path d={areaD} fill="url(#lc-fill)" />
      <path d={pathD} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3.5" fill="hsl(var(--primary))" />
          <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize="10" fill="currentColor" opacity="0.7">{p.v}</text>
          <text x={p.x} y={H - 8} textAnchor="middle" fontSize="10" fill="currentColor" opacity="0.6">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}
